import type { ReactNode } from 'react';
import { DataTable, type DataTableColumn } from '@zarya/ui';
import { t } from '@app/i18n';
import type { IconName } from '@zarya/theme';
import s from './AccessControl.module.css';

export interface CapabilityGridProps<Row> {
  label: string;
  columns: DataTableColumn<Row>[];
  rows: Row[];
  rowKey: (row: Row) => string;

  search: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder: string;

  /** Filtros próprios da aba, à esquerda da pílula (o combobox de resource, em Tools). */
  extraFilters?: ReactNode;

  grantedOnly: boolean;
  onGrantedOnlyChange: (value: boolean) => void;

  /** O contador à direita — cada aba conta a sua unidade. */
  countLabel: ReactNode;

  onClearFilters: () => void;
  filtersApplied: boolean;

  emptyIcon: IconName;
  emptyTitle: string;
  emptyMessage: string;

  /** Avisos acima da grade (o bloco fora do catálogo, o aviso do especialista). */
  children?: ReactNode;
}

/**
 * A grade de capabilities de T14 — **uma** para as duas espécies (DEF-29, 09/09).
 *
 * Desde o DEF-29 uma delegação é capability como uma tool: mesmos dois eixos (a
 * pessoa pode E a identidade pode), mesma lista do contrato, mesma decisão de
 * marcar/desmarcar. Duas grades escritas em separado divergiriam na primeira
 * mudança — foi assim que a aba de saída ficou sem a pílula «Somente concedidas»
 * que a de Tools tinha desde o começo.
 *
 * O que fica aqui é o que as duas compartilham: busca, «Somente concedidas»,
 * contador, limpar filtros e os estados vazios. O que **não** fica são as
 * colunas: uma tool tem resource, risco e limite; uma delegação tem alvo e
 * contrato, e forçar as duas na mesma linha inventaria colunas vazias.
 */
export function CapabilityGrid<Row>({
  label,
  columns,
  rows,
  rowKey,
  search,
  onSearchChange,
  searchPlaceholder,
  extraFilters,
  grantedOnly,
  onGrantedOnlyChange,
  countLabel,
  onClearFilters,
  filtersApplied,
  emptyIcon,
  emptyTitle,
  emptyMessage,
  children,
}: CapabilityGridProps<Row>) {
  return (
    <>
      {children}
      <DataTable
        label={label}
        columns={columns}
        rows={rows}
        rowKey={rowKey}
        search={search}
        onSearchChange={onSearchChange}
        searchPlaceholder={searchPlaceholder}
        filters={
          <>
            {extraFilters}
            <button
              type="button"
              className={[s.pilula, grantedOnly ? s.pilulaOn : ''].filter(Boolean).join(' ')}
              aria-pressed={grantedOnly}
              onClick={() => onGrantedOnlyChange(!grantedOnly)}
            >
              {t('identityAccess.grid.grantedOnly')}
            </button>
            <span className={s.nota}>{countLabel}</span>
          </>
        }
        onClearFilters={onClearFilters}
        filtersApplied={filtersApplied}
        emptyIcon={emptyIcon}
        emptyTitle={emptyTitle}
        emptyMessage={emptyMessage}
      />
    </>
  );
}
